import { MapPin, Phone, Clock } from "lucide-react";
import { TAVERN, HOURS } from "../../data/tavern";

export default function LocationHours() {
  const todayIndex = (new Date().getDay() + 6) % 7; // Monday = 0

  return (
    <section
      id="location"
      data-testid="location-section"
      className="border-t border-[color:var(--pk-border)] bg-[color:var(--pk-surface-2)]/40 py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[color:var(--pk-primary)]">
            ΒΡΕΙΤΕ ΜΑΣ
          </p>
          <h2 className="mt-3 font-display text-3xl font-black leading-tight tracking-tight text-[color:var(--pk-text)] sm:text-4xl md:text-5xl">
            Τοποθεσία & Ωράριο
          </h2>
        </div>

        <div className="grid gap-8 lg:grid-cols-5">
          {/* Map */}
          <div
            data-testid="map-wrap"
            className="overflow-hidden rounded-2xl border border-[color:var(--pk-border)] shadow-sm lg:col-span-3"
          >
            <iframe
              title="Χάρτης — Πρώτο Κατσίκι"
              src={`https://www.google.com/maps?q=${TAVERN.mapQuery}&output=embed`}
              width="100%"
              height="480"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-[360px] w-full border-0 md:h-[480px]"
              allowFullScreen
            />
          </div>

          {/* Info cards */}
          <div className="flex flex-col gap-5 lg:col-span-2">
            {/* Address */}
            <div
              className="rounded-2xl border border-[color:var(--pk-border)] bg-[color:var(--pk-surface)] p-6 md:p-7"
              data-testid="address-card"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--pk-primary)]/10 text-[color:var(--pk-primary)]">
                <MapPin className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <h3 className="font-display text-xl font-bold text-[color:var(--pk-text)]">
                Διεύθυνση
              </h3>
              <p className="mt-2 text-base text-[color:var(--pk-text-soft)]">
                {TAVERN.address}
              </p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${TAVERN.mapQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="directions-link"
                className="mt-4 inline-flex text-sm font-bold uppercase tracking-wide text-[color:var(--pk-primary)] underline-offset-4 hover:underline"
              >
                ΟΔΗΓΙΕΣ →
              </a>
            </div>

            {/* Phone */}
            <div
              className="rounded-2xl border border-[color:var(--pk-border)] bg-[color:var(--pk-surface)] p-6 md:p-7"
              data-testid="phone-card"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--pk-green)]/10 text-[color:var(--pk-green)]">
                <Phone className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <h3 className="font-display text-xl font-bold text-[color:var(--pk-text)]">
                Τηλέφωνο
              </h3>
              <a
                href={`tel:${TAVERN.phoneTel}`}
                data-testid="phone-link"
                className="mt-2 block font-display text-2xl font-black text-[color:var(--pk-text)] hover:text-[color:var(--pk-green)]"
              >
                {TAVERN.phone}
              </a>
            </div>
          </div>

          {/* Hours — full width below */}
          <div
            className="rounded-2xl border border-[color:var(--pk-border)] bg-[color:var(--pk-surface)] p-6 md:p-8 lg:col-span-5"
            data-testid="hours-card"
          >
            <div className="mb-6 flex items-center gap-3">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--pk-primary)]/10 text-[color:var(--pk-primary)]">
                <Clock className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <h3 className="font-display text-2xl font-bold text-[color:var(--pk-text)]">
                Ώρες Λειτουργίας
              </h3>
            </div>

            <ul className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
              {HOURS.map((h, idx) => {
                const isToday = idx === todayIndex;
                const isClosed = h.value === "Κλειστά";
                return (
                  <li
                    key={h.day}
                    data-testid={`hours-row-${idx}`}
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 transition-colors ${
                      isToday
                        ? "border-[color:var(--pk-primary)] bg-[color:var(--pk-primary)]/[0.07]"
                        : "border-[color:var(--pk-border)]"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={`font-semibold ${
                          isToday
                            ? "text-[color:var(--pk-primary)]"
                            : "text-[color:var(--pk-text)]"
                        }`}
                      >
                        {h.day}
                      </span>
                      {isToday && (
                        <span className="rounded-full bg-[color:var(--pk-primary)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                          ΣΗΜΕΡΑ
                        </span>
                      )}
                    </span>
                    <span
                      className={`text-sm font-semibold tabular-nums ${
                        isClosed
                          ? "text-[color:var(--pk-red)]"
                          : "text-[color:var(--pk-text-soft)]"
                      }`}
                    >
                      {h.value}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
